import { useState } from 'react'
import {
  LayoutDashboard,
  Users,
  FileText,
  Building2,
  Database,
  Bell,
  Search,
  TrendingUp,
  TrendingDown,
  MoreHorizontal,
  Palette,
  FilePlus,
  Globe,
  Check,
} from 'lucide-react'
import { cn } from '../../utils/cn'
import { useTheme } from '../../contexts/ThemeContext'
import { mapExplorerThemeToAppColors } from '../../utils/mapExplorerThemeToAppColors'
import Button from '../forms/Button'

type Theme = {
  id: string
  name: string
  tagline: string
  isDark: boolean
  fontHead: string
  fontBody: string
  scale: 'compact' | 'regular' | 'spacious'
  radius: string
  vars: Record<string, string>
  swatches: [string, string, string, string]
}

const THEMES: Theme[] = [
  {
    id: 'original',
    name: 'Original',
    tagline: 'Current Health Lynk — cool gray canvas, sky blue accents, Inter.',
    isDark: false,
    fontHead: "'Inter', system-ui, sans-serif",
    fontBody: "'Inter', system-ui, sans-serif",
    scale: 'regular',
    radius: '0.5rem',
    swatches: ['#f8fafc', '#ffffff', '#0ea5e9', '#0284c7'],
    vars: {
      '--t-bg': '#f8fafc',
      '--t-fg': '#334155',
      '--t-muted': '#64748b',
      '--t-card': '#ffffff',
      '--t-border': '#e2e8f0',
      '--t-sidebar-bg': '#ffffff',
      '--t-sidebar-fg': '#475569',
      '--t-sidebar-border': '#e2e8f0',
      '--t-primary': '#0ea5e9',
      '--t-primary-soft': '#f0f9ff',
      '--t-primary-fg': '#ffffff',
      '--t-accent': '#0284c7',
      '--t-success': '#16a34a',
      '--t-warn': '#d97706',
      '--t-danger': '#dc2626',
      '--t-chip-bg': '#f0f9ff',
      '--t-chip-fg': '#0369a1',
      '--t-header-bg': '#ffffff',
    },
  },
  {
    id: 'midnight',
    name: 'Midnight',
    tagline: 'Dark ops console — deep navy, electric violet, Space Grotesk headings.',
    isDark: true,
    fontHead: "'Space Grotesk', 'Inter', sans-serif",
    fontBody: "'Inter', system-ui, sans-serif",
    scale: 'regular',
    radius: '0.75rem',
    swatches: ['#0b0f1a', '#111827', '#8b5cf6', '#22d3ee'],
    vars: {
      '--t-bg': '#0b0f1a',
      '--t-fg': '#e5e7eb',
      '--t-muted': '#9aa4b2',
      '--t-card': '#111827',
      '--t-border': '#1f2937',
      '--t-sidebar-bg': '#0f1524',
      '--t-sidebar-fg': '#cbd5e1',
      '--t-sidebar-border': '#1f2937',
      '--t-primary': '#8b5cf6',
      '--t-primary-soft': '#1e1b4b',
      '--t-primary-fg': '#ffffff',
      '--t-accent': '#22d3ee',
      '--t-success': '#34d399',
      '--t-warn': '#fbbf24',
      '--t-danger': '#f87171',
      '--t-chip-bg': '#1e1b4b',
      '--t-chip-fg': '#c4b5fd',
      '--t-header-bg': '#0f1524',
    },
  },
  {
    id: 'nordic',
    name: 'Nordic',
    tagline: 'Cool, minimal, airy — Nordic palette with generous whitespace.',
    isDark: false,
    fontHead: "'Inter', system-ui, sans-serif",
    fontBody: "'Inter', system-ui, sans-serif",
    scale: 'spacious',
    radius: '0.375rem',
    swatches: ['#eceff4', '#f8f9fb', '#5e81ac', '#88c0d0'],
    vars: {
      '--t-bg': '#eceff4',
      '--t-fg': '#2e3440',
      '--t-muted': '#4c566a',
      '--t-card': '#f8f9fb',
      '--t-border': '#d8dee9',
      '--t-sidebar-bg': '#e5e9f0',
      '--t-sidebar-fg': '#3b4252',
      '--t-sidebar-border': '#d8dee9',
      '--t-primary': '#5e81ac',
      '--t-primary-soft': '#e5edf5',
      '--t-primary-fg': '#ffffff',
      '--t-accent': '#88c0d0',
      '--t-success': '#a3be8c',
      '--t-warn': '#ebcb8b',
      '--t-danger': '#bf616a',
      '--t-chip-bg': '#e5edf5',
      '--t-chip-fg': '#2e5388',
      '--t-header-bg': '#f8f9fb',
    },
  },
  {
    id: 'nordic-pro',
    name: 'Nordic Pro',
    tagline: 'Nordic palette with Sora + Manrope — compact density.',
    isDark: false,
    fontHead: "'Sora', 'Inter', sans-serif",
    fontBody: "'Manrope', 'Inter', sans-serif",
    scale: 'compact',
    radius: '0.375rem',
    swatches: ['#eceff4', '#f8f9fb', '#5e81ac', '#88c0d0'],
    vars: {
      '--t-bg': '#eceff4',
      '--t-fg': '#2e3440',
      '--t-muted': '#4c566a',
      '--t-card': '#f8f9fb',
      '--t-border': '#d8dee9',
      '--t-sidebar-bg': '#e5e9f0',
      '--t-sidebar-fg': '#3b4252',
      '--t-sidebar-border': '#d8dee9',
      '--t-primary': '#5e81ac',
      '--t-primary-soft': '#e5edf5',
      '--t-primary-fg': '#ffffff',
      '--t-accent': '#88c0d0',
      '--t-success': '#a3be8c',
      '--t-warn': '#ebcb8b',
      '--t-danger': '#bf616a',
      '--t-chip-bg': '#e5edf5',
      '--t-chip-fg': '#2e5388',
      '--t-header-bg': '#f8f9fb',
    },
  },
  {
    id: 'solar',
    name: 'Solar Warm',
    tagline: 'Warm sand + terracotta — friendly, editorial, serif headings.',
    isDark: false,
    fontHead: "'Instrument Serif', Georgia, serif",
    fontBody: "'Work Sans', 'Inter', sans-serif",
    scale: 'regular',
    radius: '0.625rem',
    swatches: ['#faf6f0', '#fffdf9', '#b45309', '#c2410c'],
    vars: {
      '--t-bg': '#faf6f0',
      '--t-fg': '#2b1e12',
      '--t-muted': '#7c6a55',
      '--t-card': '#fffdf9',
      '--t-border': '#e9dfcf',
      '--t-sidebar-bg': '#f3ebde',
      '--t-sidebar-fg': '#4b3826',
      '--t-sidebar-border': '#e2d5bd',
      '--t-primary': '#b45309',
      '--t-primary-soft': '#fdefd9',
      '--t-primary-fg': '#ffffff',
      '--t-accent': '#c2410c',
      '--t-success': '#4d7c0f',
      '--t-warn': '#a16207',
      '--t-danger': '#b91c1c',
      '--t-chip-bg': '#fdefd9',
      '--t-chip-fg': '#8a3e00',
      '--t-header-bg': '#fffdf9',
    },
  },
  {
    id: 'emerald',
    name: 'Emerald Pro',
    tagline: 'Enterprise healthcare — deep emerald, gold accents, tight density.',
    isDark: false,
    fontHead: "'Sora', 'Inter', sans-serif",
    fontBody: "'Manrope', 'Inter', sans-serif",
    scale: 'compact',
    radius: '0.375rem',
    swatches: ['#f4f7f5', '#ffffff', '#065f46', '#b45309'],
    vars: {
      '--t-bg': '#f4f7f5',
      '--t-fg': '#0f1f18',
      '--t-muted': '#5b6b63',
      '--t-card': '#ffffff',
      '--t-border': '#dbe4de',
      '--t-sidebar-bg': '#0f2a22',
      '--t-sidebar-fg': '#c8d8d1',
      '--t-sidebar-border': '#123a2e',
      '--t-primary': '#065f46',
      '--t-primary-soft': '#d1fae5',
      '--t-primary-fg': '#ffffff',
      '--t-accent': '#b45309',
      '--t-success': '#059669',
      '--t-warn': '#d97706',
      '--t-danger': '#b91c1c',
      '--t-chip-bg': '#d1fae5',
      '--t-chip-fg': '#065f46',
      '--t-header-bg': '#ffffff',
    },
  },
  {
    id: 'rose-noir',
    name: 'Rose Noir',
    tagline: 'Editorial dark — charcoal canvas with rose gold highlights.',
    isDark: true,
    fontHead: "'Cormorant Garamond', Georgia, serif",
    fontBody: "'Karla', 'Inter', sans-serif",
    scale: 'regular',
    radius: '0.5rem',
    swatches: ['#141216', '#1c1a1f', '#e11d74', '#f59e0b'],
    vars: {
      '--t-bg': '#141216',
      '--t-fg': '#f5f0ee',
      '--t-muted': '#a89ea0',
      '--t-card': '#1c1a1f',
      '--t-border': '#2a262d',
      '--t-sidebar-bg': '#100e12',
      '--t-sidebar-fg': '#e8dcde',
      '--t-sidebar-border': '#2a262d',
      '--t-primary': '#e11d74',
      '--t-primary-soft': '#3b0f27',
      '--t-primary-fg': '#ffffff',
      '--t-accent': '#f59e0b',
      '--t-success': '#84cc16',
      '--t-warn': '#f59e0b',
      '--t-danger': '#ef4444',
      '--t-chip-bg': '#3b0f27',
      '--t-chip-fg': '#f9a8c9',
      '--t-header-bg': '#1c1a1f',
    },
  },
]

const NAV_GROUPS: {
  label: string
  items: { title: string; icon: React.ElementType; active?: boolean }[]
}[] = [
  {
    label: 'Overview',
    items: [{ title: 'Dashboard', icon: LayoutDashboard, active: true }],
  },
  {
    label: 'Patients',
    items: [
      { title: 'Patients', icon: Users },
      { title: 'Patient Intake', icon: FilePlus },
      { title: 'Prior Auth', icon: FileText },
    ],
  },
  {
    label: 'Configuration',
    items: [
      { title: 'Payor Configuration', icon: Building2 },
      { title: 'Masters', icon: Database },
      { title: 'Eligibility Services', icon: Globe },
    ],
  },
]

const METRICS = [
  { label: 'Active patients', value: '8,241', delta: '+4.1%', up: true },
  { label: 'Pending eligibility', value: '126', delta: '-6.3%', up: false },
  { label: 'Open prior auths', value: '38', delta: '+3', up: false },
  { label: 'Coverage verified', value: '94.2%', delta: '+0.6%', up: true },
]

const ROWS = [
  { id: 'ELG-2042', patient: 'Ava Chen', payor: 'Aetna', service: 'MRI Lumbar', risk: 'Low', status: 'Pending' },
  { id: 'ELG-2041', patient: 'Marcus Wong', payor: 'UHC', service: 'CT Abdomen', risk: 'High', status: 'Escalated' },
  { id: 'ELG-2040', patient: 'Priya Shah', payor: 'Cigna', service: 'Physical Therapy', risk: 'Medium', status: 'Approved' },
  { id: 'ELG-2039', patient: 'Diego Alvarez', payor: 'BCBS', service: 'Lab Panel', risk: 'Low', status: 'Approved' },
  { id: 'ELG-2038', patient: 'Lin Park', payor: 'Humana', service: 'Specialist Visit', risk: 'Medium', status: 'Pending' },
  { id: 'ELG-2037', patient: 'Omar Haddad', payor: 'Medicare', service: 'DME Order', risk: 'High', status: 'Pending' },
]

export default function ThemesExplorer({ onApplied }: { onApplied?: (themeName: string) => void }) {
  const { theme: appTheme, applyThemePreset } = useTheme()
  const [activeId, setActiveId] = useState(appTheme.explorerThemeId ?? 'original')
  const active = THEMES.find((t) => t.id === activeId) ?? THEMES[0]
  const isActiveApplied = appTheme.explorerThemeId === active.id

  const handleApply = () => {
    const colors = mapExplorerThemeToAppColors(active.vars, active.isDark)
    applyThemePreset(colors, active.isDark, active.id)
    onApplied?.(active.name)
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-baseline gap-3">
            <h3 className="text-lg font-semibold tracking-tight text-gray-900 dark:text-gray-100">
              Themes explorer
            </h3>
            <span className="text-sm text-gray-500 dark:text-gray-400">{THEMES.length} themes</span>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Preview theme directions in the sandbox below, then click Apply to update the live app.
          </p>
        </div>
        <Button onClick={handleApply} size="sm" disabled={isActiveApplied}>
          {isActiveApplied ? 'Applied' : `Apply ${active.name}`}
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {THEMES.map((t) => {
          const isApplied = appTheme.explorerThemeId === t.id
          return (
          <button
            key={t.id}
            type="button"
            onClick={() => setActiveId(t.id)}
            className={cn(
              'group relative rounded-xl border bg-white dark:bg-gray-800 p-3 text-left transition-all hover:shadow-sm',
              active.id === t.id
                ? 'border-primary-500 ring-2 ring-primary-500/30'
                : 'border-gray-200 dark:border-gray-700 hover:border-primary-500/40',
              isApplied && 'ring-1 ring-green-500/40',
            )}
          >
            {isApplied && (
              <span className="absolute top-2 right-2 flex items-center gap-0.5 rounded-full bg-green-100 dark:bg-green-900/40 px-1.5 py-0.5 text-[10px] font-semibold text-green-700 dark:text-green-400">
                <Check className="w-3 h-3" />
                Live
              </span>
            )}
            <div className="flex -space-x-1.5 mb-2">
              {t.swatches.map((c, i) => (
                <span
                  key={i}
                  className="w-6 h-6 rounded-full border-2 border-white dark:border-gray-800"
                  style={{ background: c }}
                />
              ))}
            </div>
            <div className="text-sm font-semibold flex items-center gap-1.5 text-gray-900 dark:text-gray-100">
              <Palette className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" />
              {t.name}
            </div>
            <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">{t.tagline}</div>
          </button>
          )
        })}
      </div>

      <ThemePreview theme={active} />
    </div>
  )
}

function ThemePreview({ theme }: { theme: Theme }) {
  const scaleTokens = {
    compact: { pad: '10px 12px', gap: '10px', text: '13px' },
    regular: { pad: '14px 16px', gap: '14px', text: '14px' },
    spacious: { pad: '18px 20px', gap: '18px', text: '14.5px' },
  }[theme.scale]

  return (
    <div
      className="rounded-2xl border overflow-hidden shadow-sm"
      style={{
        ...(theme.vars as React.CSSProperties),
        background: 'var(--t-bg)',
        color: 'var(--t-fg)',
        fontFamily: theme.fontBody,
        borderColor: 'var(--t-border)',
      }}
    >
      <div style={{ display: 'grid', gridTemplateColumns: '240px 1fr', minHeight: 620 }}>
        <aside
          style={{
            background: 'var(--t-sidebar-bg)',
            color: 'var(--t-sidebar-fg)',
            borderRight: '1px solid var(--t-sidebar-border)',
            padding: '16px 12px',
            fontSize: scaleTokens.text,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '6px 8px 14px',
              fontFamily: theme.fontHead,
              fontWeight: 600,
              fontSize: 16,
              letterSpacing: '-0.01em',
            }}
          >
            <span
              style={{
                width: 26,
                height: 26,
                borderRadius: theme.radius,
                background: 'var(--t-primary)',
                color: 'var(--t-primary-fg)',
                display: 'grid',
                placeItems: 'center',
                fontSize: 12,
                fontWeight: 700,
              }}
            >
              H
            </span>
            Health Lynk
          </div>

          {NAV_GROUPS.map((g) => (
            <div key={g.label} style={{ marginTop: 14 }}>
              <div
                style={{
                  fontSize: 10,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  color: 'var(--t-muted)',
                  padding: '0 8px 6px',
                  fontWeight: 600,
                }}
              >
                {g.label}
              </div>
              {g.items.map((it) => {
                const Icon = it.icon
                return (
                  <div
                    key={it.title}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '8px 10px',
                      borderRadius: theme.radius,
                      background: it.active ? 'var(--t-primary-soft)' : 'transparent',
                      color: it.active ? 'var(--t-primary)' : 'var(--t-sidebar-fg)',
                      fontWeight: it.active ? 600 : 500,
                    }}
                  >
                    <Icon size={16} />
                    {it.title}
                  </div>
                )
              })}
            </div>
          ))}
        </aside>

        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <header
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '12px 20px',
              background: 'var(--t-header-bg)',
              borderBottom: '1px solid var(--t-border)',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '6px 10px',
                background: 'var(--t-bg)',
                border: '1px solid var(--t-border)',
                borderRadius: theme.radius,
                flex: 1,
                maxWidth: 360,
                color: 'var(--t-muted)',
                fontSize: 13,
              }}
            >
              <Search size={14} />
              Search patients, payors, eligibility…
            </div>
            <button
              type="button"
              style={{
                background: 'transparent',
                border: '1px solid var(--t-border)',
                borderRadius: theme.radius,
                width: 32,
                height: 32,
                display: 'grid',
                placeItems: 'center',
                color: 'var(--t-muted)',
              }}
            >
              <Bell size={15} />
            </button>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                background: 'var(--t-primary)',
                color: 'var(--t-primary-fg)',
                display: 'grid',
                placeItems: 'center',
                fontWeight: 600,
                fontSize: 12,
              }}
            >
              AK
            </div>
          </header>

          <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: scaleTokens.gap }}>
            <div>
              <div
                style={{
                  fontFamily: theme.fontHead,
                  fontSize: 22,
                  fontWeight: 600,
                  letterSpacing: '-0.015em',
                }}
              >
                Provider dashboard
              </div>
              <div style={{ color: 'var(--t-muted)', fontSize: 13, marginTop: 2 }}>
                {theme.name} theme — {theme.tagline}
              </div>
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, minmax(0,1fr))',
                gap: scaleTokens.gap,
              }}
            >
              {METRICS.map((m) => (
                <div
                  key={m.label}
                  style={{
                    background: 'var(--t-card)',
                    border: '1px solid var(--t-border)',
                    borderRadius: theme.radius,
                    padding: scaleTokens.pad,
                  }}
                >
                  <div
                    style={{
                      color: 'var(--t-muted)',
                      fontSize: 11,
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                      fontWeight: 600,
                    }}
                  >
                    {m.label}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 6 }}>
                    <div
                      style={{
                        fontFamily: theme.fontHead,
                        fontSize: 24,
                        fontWeight: 600,
                        letterSpacing: '-0.01em',
                      }}
                    >
                      {m.value}
                    </div>
                    <div
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 3,
                        fontSize: 11,
                        color: m.up ? 'var(--t-success)' : 'var(--t-danger)',
                        fontWeight: 600,
                      }}
                    >
                      {m.up ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                      {m.delta}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div
              style={{
                background: 'var(--t-card)',
                border: '1px solid var(--t-border)',
                borderRadius: theme.radius,
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px 16px',
                  borderBottom: '1px solid var(--t-border)',
                }}
              >
                <div style={{ fontFamily: theme.fontHead, fontSize: 14, fontWeight: 600 }}>
                  Recent eligibility checks
                </div>
                <button
                  type="button"
                  style={{
                    background: 'var(--t-primary)',
                    color: 'var(--t-primary-fg)',
                    border: 'none',
                    borderRadius: theme.radius,
                    padding: '6px 12px',
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  New check
                </button>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: scaleTokens.text }}>
                <thead>
                  <tr style={{ background: 'var(--t-bg)', color: 'var(--t-muted)', textAlign: 'left' }}>
                    {['Request', 'Patient', 'Payor', 'Service', 'Risk', 'Status', ''].map((h) => (
                      <th
                        key={h}
                        style={{
                          padding: '10px 16px',
                          fontWeight: 600,
                          fontSize: 11,
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {ROWS.map((r, i) => (
                    <tr key={r.id} style={{ borderTop: i === 0 ? 'none' : '1px solid var(--t-border)' }}>
                      <td
                        style={{
                          padding: scaleTokens.pad,
                          fontFamily: 'ui-monospace, Menlo, monospace',
                          fontSize: 12,
                          color: 'var(--t-muted)',
                        }}
                      >
                        {r.id}
                      </td>
                      <td style={{ padding: scaleTokens.pad, fontWeight: 500 }}>{r.patient}</td>
                      <td style={{ padding: scaleTokens.pad, color: 'var(--t-muted)' }}>{r.payor}</td>
                      <td style={{ padding: scaleTokens.pad }}>{r.service}</td>
                      <td style={{ padding: scaleTokens.pad }}>
                        <RiskChip level={r.risk} radius={theme.radius} />
                      </td>
                      <td style={{ padding: scaleTokens.pad }}>
                        <StatusChip status={r.status} radius={theme.radius} />
                      </td>
                      <td style={{ padding: scaleTokens.pad, textAlign: 'right' }}>
                        <MoreHorizontal size={16} color="var(--t-muted)" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div
              style={{
                background: 'var(--t-card)',
                border: '1px solid var(--t-border)',
                borderRadius: theme.radius,
                padding: scaleTokens.pad,
                display: 'flex',
                alignItems: 'center',
                gap: 20,
                flexWrap: 'wrap',
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: 10,
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    color: 'var(--t-muted)',
                    fontWeight: 600,
                  }}
                >
                  Heading
                </div>
                <div style={{ fontFamily: theme.fontHead, fontSize: 15, marginTop: 4 }}>{theme.fontHead}</div>
              </div>
              <div>
                <div
                  style={{
                    fontSize: 10,
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    color: 'var(--t-muted)',
                    fontWeight: 600,
                  }}
                >
                  Body
                </div>
                <div style={{ fontFamily: theme.fontBody, fontSize: 13, marginTop: 4 }}>{theme.fontBody}</div>
              </div>
              <div>
                <div
                  style={{
                    fontSize: 10,
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    color: 'var(--t-muted)',
                    fontWeight: 600,
                  }}
                >
                  Density
                </div>
                <div style={{ fontSize: 13, marginTop: 4, textTransform: 'capitalize' }}>{theme.scale}</div>
              </div>
              <div>
                <div
                  style={{
                    fontSize: 10,
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    color: 'var(--t-muted)',
                    fontWeight: 600,
                  }}
                >
                  Radius
                </div>
                <div style={{ fontSize: 13, marginTop: 4 }}>{theme.radius}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function RiskChip({ level, radius }: { level: string; radius: string }) {
  const map: Record<string, { bg: string; fg: string }> = {
    Low: { bg: 'color-mix(in oklab, var(--t-success) 15%, transparent)', fg: 'var(--t-success)' },
    Medium: { bg: 'color-mix(in oklab, var(--t-warn) 18%, transparent)', fg: 'var(--t-warn)' },
    High: { bg: 'color-mix(in oklab, var(--t-danger) 18%, transparent)', fg: 'var(--t-danger)' },
  }
  const s = map[level] ?? map.Low
  return (
    <span
      style={{
        background: s.bg,
        color: s.fg,
        padding: '2px 8px',
        borderRadius: radius,
        fontSize: 11,
        fontWeight: 600,
      }}
    >
      {level}
    </span>
  )
}

function StatusChip({ status, radius }: { status: string; radius: string }) {
  const map: Record<string, { bg: string; fg: string }> = {
    Pending: { bg: 'var(--t-chip-bg)', fg: 'var(--t-chip-fg)' },
    Approved: { bg: 'color-mix(in oklab, var(--t-success) 15%, transparent)', fg: 'var(--t-success)' },
    Escalated: { bg: 'color-mix(in oklab, var(--t-danger) 18%, transparent)', fg: 'var(--t-danger)' },
  }
  const s = map[status] ?? map.Pending
  return (
    <span
      style={{
        background: s.bg,
        color: s.fg,
        padding: '2px 8px',
        borderRadius: radius,
        fontSize: 11,
        fontWeight: 600,
      }}
    >
      {status}
    </span>
  )
}
